// /composables/useArticles.ts
import type { TypeArticleWithoutUnresolvableLinksResponse } from '~/types/contentful'
import { computed, ref, watch, unref, type Ref } from 'vue'
import { clearNuxtData } from '#app'
import {
  fetchAccessibleArticleIds,
  fetchArticlesByIdsInBatches,
  filterAccessibleArticles,
} from '~/services/articles' // adjust path

type MaybeRef<T> = T | Ref<T>

type UseArticlesParams = {
  searchQuery?: MaybeRef<string | null | undefined>
  selectedCategory?: MaybeRef<string | null | undefined>
  pageSize?: number
  keyScope?: MaybeRef<string | null | undefined>
  clientOnly?: MaybeRef<boolean | null | undefined>
}

type BundlePayload = {
  entryIds: string[]
  list: TypeArticleWithoutUnresolvableLinksResponse[]
  fetchedAt: number
}

export function useArticles(params: UseArticlesParams = {}) {
  const { $client, $userAccessCheck, $visibility } = useNuxtApp()
  const { locale } = useI18n()
  // const cookieStore = useCookieStore()
  const { highestUserLevel } = storeToRefs(useUserStore())

  // unwrap inputs
  const q = computed(() => String(unref(params.searchQuery) ?? '').trim())
  const c = computed(() => String(unref(params.selectedCategory) ?? '').trim())
  const search = computed(() => (q.value.length >= 2 ? q.value : ''))
  const category = computed(() => c.value || '')
  const keyScope = computed(() => String(unref(params.keyScope) ?? '').trim())
  const clientOnly = computed(() => Boolean(unref(params.clientOnly)))
  const asyncKey = computed(
    () =>
      `articles:bundle:${keyScope.value || 'default'}:${locale.value}:${search.value}:${category.value}`,
  )

  // paging
  const pageSize = params.pageSize ?? 12
  const currentPage = ref(1)

  const {
    data: bundle,
    status,
    refresh,
  } = useAsyncData<BundlePayload>(
    asyncKey,
    async () => {
      // const pageSize = 100
      // let from = 0
      // let total = 0
      // const entryIds: string[] = []

      // do {
      //
      //   let searchEndpoint = `/api/search?lang=${locale.value}&type=article&size=${pageSize}&from=${from}&sort=latest`

      //   if (cookieStore.countryCookie)
      //     searchEndpoint += `&country=${cookieStore.countryCookie}`
      //   if (search.value)
      //     searchEndpoint += `&q=${encodeURIComponent(search.value)}`
      //   if (category.value)
      //     searchEndpoint += `&tag=${encodeURIComponent(category.value)}`

      //   const resultEntryIds = await $fetch<{
      //     ids: string[]
      //     total: {
      //       value: number
      //       relation: string
      //     }
      //   }>(searchEndpoint)

      //   const ids = resultEntryIds?.ids || []
      //   if (!ids.length) break

      //   total = resultEntryIds?.total?.value || 0
      //   entryIds.push(...ids)
      //   from += ids.length
      // } while (entryIds.length < total)

      const entryIds =
        (await fetchAccessibleArticleIds({
          $client,
          $userAccessCheck,
          $visibility,
          locale: locale.value,
          highestUserLevel: highestUserLevel.value,
          searchQuery: search.value,
          selectedCategory: category.value,
        })) ?? []

      const items = entryIds.length
        ? await fetchArticlesByIdsInBatches({
            $client,
            locale: locale.value,
            ids: entryIds,
          })
        : []

      const accessibleItems = filterAccessibleArticles({
        articles: items,
        $userAccessCheck,
        $visibility,
        selectedCategory: category.value,
        highestUserLevel: highestUserLevel.value,
      })

      const orderMap = new Map(entryIds.map((id, i) => [id, i]))
      const list = accessibleItems
        .slice()
        .sort(
          (a, b) =>
            (orderMap.get(a.sys.id) ?? 0) - (orderMap.get(b.sys.id) ?? 0),
        )

      return {
        entryIds,
        list,
        fetchedAt: Date.now(),
      }
    },
    {
      default: () => ({
        entryIds: [],
        list: [],
        fetchedAt: Date.now(),
      }),
      server: !clientOnly.value,
      lazy: false,
    },
  )

  watch([q, c, locale, keyScope], async () => {
    currentPage.value = 1
    if (import.meta.dev) {
      // Dev-only cache invalidation keeps HMR/local hydration deterministic.
      // See docs/async-data-caching.md for rationale and tradeoffs.
      clearNuxtData(asyncKey.value)
      await refresh()
    }
  })

  const allArticles = computed(() => bundle.value?.list ?? [])
  const pagedArticles = computed(() =>
    allArticles.value.slice(0, pageSize * currentPage.value),
  )

  const loadMore = () => {
    currentPage.value++
  }

  return {
    // status (single source)
    idsStatus: status,
    articlesStatus: status,

    // data
    allArticles,
    pagedArticles,

    // controls
    currentPage,
    loadMore,
  }
}
